/**
 * Exporte (Auftrag F09). Maschinenformat bewusst getrennt vom Anzeigeformat:
 *  - CSV: UTF-8, Trennzeichen Semikolon, Dezimalpunkt, keine Tausendertrennung,
 *    ISO-Daten, eine Zeile je Gebäude und Jahr.
 *  - JSON: vollständiger Analysezustand mit Schema-Version, Herkunft,
 *    Resultaten und optional normalisierten Eingabedaten (Int16, Base64, SHA-256).
 */
import { CALC_VERSION, MODELS, STATE_SCHEMA_VERSION, type HourPoint, type YearDetail, type YearMetrics } from '../core/domain/types';
import type { ExportedCell, ResultEntry } from '../core/worker/protocol';
import { persistable, request, resultFor, useApp } from './store';
import { parsePersisted, type PersistedState } from './persist';
import type { Building, AnalysisConfig } from '../core/domain/types';
import { guidanceValue, type FutureIndicator, type FutureResult } from '../core/future/types';
import type { PortfolioRow } from './futurePortfolio';
import { buildingReference, guidance, guidanceAllowed, INDICATOR_METRIC, useFuture } from './future';

export const APP_VERSION = '1.1.0';
export const CSV_DELIMITER = ';';

const YEAR_COLUMNS: (keyof YearMetrics)[] = [
  'year',
  'complete',
  'daysValid',
  'daysExpected',
  'nightsValid',
  'nightsExpected',
  'seasonNightsValid',
  'seasonNightsExpected',
  'hoursMissing',
  'hoursConflict',
  'hdd',
  'cdd',
  'hotDays',
  'heatEvents',
  'heatDays',
  'heatLongest',
  'warmNights',
  'coolHours',
  'coolKh',
  'tMean',
  'tropicalNights',
  'veryHotDays',
  'txx',
];

const CSV_HEADER = [
  'gebaeude_id',
  'bezeichnung',
  'egid',
  'lat',
  'lon',
  'standorthoehe_m',
  'modell',
  'zelle_lat',
  'zelle_lon',
  'hoehenkorrektur_K',
  'zeitzone',
  'jahr',
  'status',
  'tage_gueltig',
  'tage_erwartet',
  'naechte_gueltig',
  'naechte_erwartet',
  'saisonnaechte_gueltig',
  'saisonnaechte_erwartet',
  'stunden_fehlend',
  'stunden_widerspruch',
  'hgt_Kd',
  'kgt_Kd',
  'heisse_tage',
  'hitzeperioden',
  'hitzeperiodentage',
  'laengste_hitzeperiode_tage',
  'warme_naechte',
  'kuehlstunden_h',
  'kuehlpotenzial_Kh',
  'jahresmittel_C',
  'tropennaechte',
  'sehr_heisse_tage',
  'txx_C',
];

function csvCell(v: unknown): string {
  if (v === null || v === undefined) return '';
  if (typeof v === 'number') return Number.isFinite(v) ? String(v) : '';
  if (typeof v === 'boolean') return v ? '1' : '0';
  const s = String(v);
  return /[";\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

export function buildYearCsv(): string {
  const s = useApp.getState();
  const lines = [CSV_HEADER.join(CSV_DELIMITER)];
  for (const b of s.buildings) {
    const r = resultFor(s, b.id);
    const p = s.placements.find((x) => x.buildingId === b.id && x.model === s.config.model);
    if (!r || !p) continue;
    for (const y of r.years) {
      if (y.year < s.config.periodStart || y.year > s.config.periodEnd) continue;
      const status = y.complete ? 'vollstaendig' : y.daysValid > 0 ? 'teilwert' : y.loaded ? 'keine_daten' : 'nicht_geladen';
      const row = [b.id, b.label, b.egid ?? '', b.lat, b.lon, p.targetElevation, s.config.model, p.cellLat, p.cellLon, r.heightCorrectionK, p.timezone, ...YEAR_COLUMNS.slice(0, 1).map((k) => y[k]), status, ...YEAR_COLUMNS.slice(2).map((k) => y[k])];
      lines.push(row.map(csvCell).join(CSV_DELIMITER));
    }
  }
  return lines.join('\r\n') + '\r\n';
}

/** Tageswerte eines Jahres: ISO-Datum (lokaler Kalendertag), °C mit Dezimalpunkt. */
export function buildDayCsv(detail: YearDetail, buildingId: string, label: string): string {
  const head = ['gebaeude_id', 'bezeichnung', 'datum_lokal', 'status', 'stunden_gueltig', 'stunden_erwartet', 'tmin_C', 'tmittel_C', 'tmax_C'];
  const rows = detail.days.map((d) => [buildingId, label, d.date, d.valid ? 'gueltig' : d.loaded ? 'ungueltig' : 'nicht_geladen', d.hours, d.expected, d.min, d.mean, d.max]);
  return [head, ...rows].map((r) => r.map(csvCell).join(CSV_DELIMITER)).join('\r\n') + '\r\n';
}

/** Stundenwerte: UTC-Zeitstempel ISO 8601 mit Z, Ortszeit ohne Zone, °C. */
export function buildHourCsv(points: HourPoint[], buildingId: string, label: string, timezone: string): string {
  const head = ['gebaeude_id', 'bezeichnung', 'zeit_utc', 'zeit_lokal', 'zeitzone', 'temperatur_C', 'status', 'nachtfenster'];
  const rows = points.map((p) => [buildingId, label, p.utc, p.local, timezone, p.t, p.status === 'ok' ? 'ok' : p.status === 'fehlend' ? 'fehlend' : p.status === 'widerspruch' ? 'widerspruch' : 'nicht_geladen', p.inNight]);
  return [head, ...rows].map((r) => r.map(csvCell).join(CSV_DELIMITER)).join('\r\n') + '\r\n';
}

/**
 * Zukunftsklima eines Gebäudes: eine Zeile je Kennzahl und Stufe bzw.
 * Szenario-Periode, mit Änderung (Median, P10, P90), Referenzen und Richtwert.
 */
export function buildFutureCsv(building: Building, r: FutureResult, result: ResultEntry | undefined, cfg: AnalysisConfig): string {
  const head = [
    'gebaeude_id',
    'bezeichnung',
    'quelle',
    'lizenz',
    'ort_art',
    'ort',
    'ort_lat',
    'ort_lon',
    'distanz_km',
    'hoehenunterschied_m',
    'repraesentativ',
    'referenzperiode',
    'kennzahl',
    'definition',
    'einheit',
    'referenz_gebaeude_era5land',
    'referenz_quelle',
    'stufe',
    'globale_erwaermung_C',
    'erwaermung_naeherung',
    'aenderung_median',
    'aenderung_p10',
    'aenderung_p90',
    'modelle',
    'robustheit',
    'richtwert',
    'richtwert_p10',
    'richtwert_p90',
    'zukunft_quelle',
    'berechnet',
  ];
  const ref = buildingReference(result, r);
  const rows: unknown[][] = [];
  for (const row of r.rows) {
    const bref = ref && ref.status !== 'ungenuegend' ? ref.means[INDICATOR_METRIC[row.indicator]] : undefined;
    const allowed = guidanceAllowed(r, row.indicator, cfg).ok;
    for (const l of r.levels) {
      const g = guidance(row, l.key, bref, allowed);
      rows.push([
        building.id,
        building.label,
        r.sourceLabel,
        r.licence,
        r.location.kind === 'station' ? 'station' : 'zelle',
        r.location.name,
        r.location.lat,
        r.location.lon,
        Math.round(r.location.distanceKm * 10) / 10,
        r.location.elevationDiffM ?? '',
        r.location.representative,
        r.referencePeriod,
        row.indicator,
        row.definition,
        row.unit,
        bref ?? '',
        row.sourceRef ?? '',
        l.label,
        l.gwl,
        l.gwlApprox,
        g.delta?.p50 ?? '',
        g.delta?.p10 ?? '',
        g.delta?.p90 ?? '',
        g.delta?.n ?? '',
        row.robustness?.[l.key] ?? '',
        g.value ?? '',
        g.range?.[0] ?? '',
        g.range?.[1] ?? '',
        row.sourceRef !== null && g.delta ? guidanceValue(row.indicator, row.sourceRef, g.delta.p50) : '',
        r.computedAt,
      ]);
    }
  }
  return [head, ...rows].map((x) => x.map(csvCell).join(CSV_DELIMITER)).join('\r\n') + '\r\n';
}

/** Zukunftsklima im Portfolio: eine Zeile je Gebäude für die gewählte Kennzahl und Stufe. */
export function buildFuturePortfolioCsv(rows: PortfolioRow[], indicator: FutureIndicator): string {
  const head = [
    'gebaeude_id',
    'bezeichnung',
    'egid',
    'lat',
    'lon',
    'quelle',
    'ort',
    'distanz_km',
    'repraesentativ',
    'referenzperiode',
    'kennzahl',
    'stufe',
    'globale_erwaermung_C',
    'erwaermung_naeherung',
    'referenz_gebaeude_era5land',
    'aenderung_median',
    'aenderung_p10',
    'aenderung_p90',
    'modelle',
    'richtwert',
    'richtwert_p10',
    'richtwert_p90',
    'zukunft_quelle',
    'status',
  ];
  const out = rows.map((r) => [
    r.building.id,
    r.building.label,
    r.building.egid ?? '',
    r.building.lat,
    r.building.lon,
    r.group.source === 'ch2025' ? 'CH2025' : 'Weltbank CCKP',
    r.result?.location.name ?? r.group.label,
    r.distanceKm === undefined ? '' : Math.round(r.distanceKm * 10) / 10,
    r.representative ?? '',
    r.result?.referencePeriod ?? '',
    indicator,
    r.level?.label ?? '',
    r.level?.gwl ?? '',
    r.level?.gwlApprox ?? '',
    r.buildingRef ?? '',
    r.delta?.p50 ?? '',
    r.delta?.p10 ?? '',
    r.delta?.p90 ?? '',
    r.delta?.n ?? '',
    r.guide.value ?? '',
    r.guide.range?.[0] ?? '',
    r.guide.range?.[1] ?? '',
    r.sourceFuture ?? '',
    r.status,
  ]);
  return [head, ...out].map((x) => x.map(csvCell).join(CSV_DELIMITER)).join('\r\n') + '\r\n';
}

export interface AnalysisExport {
  schema: 'klimaraum-analyse';
  schemaVersion: number;
  appVersion: string;
  calcVersion: string;
  exportedAt: string;
  mode: string;
  source: { provider: string; dataset: string; model: string; licence: string; terms: string };
  state: PersistedState;
  results: { buildingId: string; cellKey: string; heightCorrectionK: number; years: YearMetrics[]; reference: ResultEntry['reference']; lastCompleteYear: number | null }[];
  provenance: { cellKey: string; elevation: number | null; chunks: ExportedCell['chunks'] }[];
  inputData: ExportedCell[] | null;
  inputDataNote: string;
  /** Zukunftsklima der laufenden Sitzung (Ausbaustufe B), mit Herkunft der Quelldateien */
  future?: FutureResult[];
}

export async function buildAnalysisJson(withData: boolean): Promise<AnalysisExport> {
  const s = useApp.getState();
  const cellKeys = [...new Set(s.placements.filter((p) => p.model === s.config.model).map((p) => p.cellKey))];
  const cells = await request<ExportedCell[]>({ kind: 'exportData', cellKeys });
  const results = s.buildings
    .map((b) => resultFor(s, b.id))
    .filter((r): r is ResultEntry => !!r)
    .map((r) => ({ buildingId: r.buildingId, cellKey: r.cellKey, heightCorrectionK: r.heightCorrectionK, years: r.years, reference: r.reference, lastCompleteYear: r.lastCompleteYear }));
  return {
    schema: 'klimaraum-analyse',
    schemaVersion: STATE_SCHEMA_VERSION,
    appVersion: APP_VERSION,
    calcVersion: CALC_VERSION,
    exportedAt: new Date().toISOString(),
    mode: s.mode,
    source: {
      provider: 'Open-Meteo Historical Weather API (https://open-meteo.com/en/docs/historical-weather-api)',
      dataset: MODELS[s.config.model].dataset,
      model: s.config.model,
      licence: 'CC BY 4.0 – Quelle: Open-Meteo.com; enthält modifizierte Copernicus Climate Change Service Informationen',
      terms: 'Freie API nur für nicht-kommerzielle Nutzung (https://open-meteo.com/en/terms)',
    },
    state: persistable(s, new Date().toISOString()),
    results,
    provenance: cells.map((c) => ({ cellKey: c.key, elevation: c.elevation, chunks: c.chunks })),
    inputData: withData ? cells : null,
    inputDataNote: withData
      ? 'Normalisierte stündliche Rohwerte je Rasterzelle (vor Höhenkorrektur), Int16 Little Endian in Zehntelgrad, -32768 fehlend, -32767 nicht geladen, -32766 Widerspruch; SHA-256 über die Bytes.'
      : 'Ohne Eingabedaten: Reproduktion nur über erneuten Abruf; Abrufstücke sind über SHA-256 in provenance referenziert. Veränderte Quelldaten werden beim Vergleich der Hashes erkannt.',
    future: Object.values(useFuture.getState().entries)
      .map((e) => e.result)
      .filter((r): r is FutureResult => !!r),
  };
}

export function download(name: string, content: string | Blob, type: string) {
  const blob = typeof content === 'string' ? new Blob([content], { type }) : content;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

export function stamp(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}_${p(d.getHours())}${p(d.getMinutes())}`;
}

export interface ImportOutcome {
  buildings: number;
  withData: boolean;
  check: Promise<{ compared: number; identical: boolean; maxDiff: number; missing: number }>;
}

/** Liest einen JSON-Analyseexport ein und prüft, ob die Kennzahlen reproduziert werden. */
export async function importAnalysis(text: string): Promise<ImportOutcome> {
  let raw: AnalysisExport;
  try {
    raw = JSON.parse(text);
  } catch {
    throw new Error('Datei ist kein gültiges JSON.');
  }
  if (raw?.schema !== 'klimaraum-analyse') throw new Error('Keine KLIMA / RAUM-Analysedatei.');
  if (raw.schemaVersion !== STATE_SCHEMA_VERSION) throw new Error(`Schema-Version ${raw.schemaVersion} wird nicht unterstützt (erwartet ${STATE_SCHEMA_VERSION}).`);
  const parsed = parsePersisted(raw.state);
  if (!parsed.ok) throw new Error(`Analysezustand ungültig: ${parsed.reason === 'none' ? 'leer' : parsed.detail}`);
  const s = useApp.getState();
  if (raw.mode !== s.mode) await s.setMode(raw.mode === 'demo' ? 'demo' : 'live');
  useApp.getState().replaceState(parsed.state);
  const withData = Array.isArray(raw.inputData) && raw.inputData.length > 0;
  if (withData) await request({ kind: 'importData', cells: raw.inputData! });
  const expected = raw.results;
  const check = new Promise<{ compared: number; identical: boolean; maxDiff: number; missing: number }>((resolve) => {
    const started = Date.now();
    const evaluate = () => {
      const st = useApp.getState();
      let compared = 0;
      let maxDiff = 0;
      let missing = 0;
      for (const e of expected) {
        const r = st.results[e.buildingId];
        if (!r || r.version !== st.version) {
          missing++;
          continue;
        }
        for (const ey of e.years) {
          if (!ey.complete) continue;
          const ry = r.years.find((y) => y.year === ey.year);
          if (!ry || !ry.complete) {
            missing++;
            continue;
          }
          for (const k of ['hdd', 'cdd', 'hotDays', 'heatEvents', 'heatDays', 'warmNights', 'coolHours', 'coolKh'] as const) {
            maxDiff = Math.max(maxDiff, Math.abs((ry[k] as number) - (ey[k] as number)));
            compared++;
          }
        }
      }
      return { compared, identical: missing === 0 && maxDiff <= 1e-9, maxDiff, missing };
    };
    const unsub = useApp.subscribe(() => {
      const r = evaluate();
      if (r.missing === 0 || Date.now() - started > 120_000) {
        unsub();
        resolve(r);
      }
    });
    setTimeout(() => {
      unsub();
      resolve(evaluate());
    }, 120_000);
  });
  return { buildings: parsed.state.buildings.length, withData, check };
}
